/**
 * O sino e a página /notificacoes (spec 037) no SPA: o feed da PRÓPRIA pessoa,
 * as Últimas respostas e as duas escritas de lido. O estado de lido mora no
 * servidor — o `localStorage` não guarda nada disso (a marca antiga sai na
 * primeira carga, `forgetLegacySeenAt`).
 *
 * A API ainda sem /notifications (o SPA sobe no Pages minutos antes da API — a
 * regra de mudança de dois lados): a consulta responde 404, o sino cai no feed
 * antigo (/tickets/events/recent), só para leitura — sem contador e sem
 * "Marcar como lidas" —, e nenhuma escrita é tentada.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NOTIFICATIONS_BELL_SIZE,
  type NotificationReadState,
  type NotificationReplies,
  type NotificationsPage,
  type NotificationsUnread,
  type NotificationEventKind,
} from '@erp/shared';
import { api } from '@/lib/api-client';

export const NOTIFICATIONS_KEY = ['erp', 'notifications'] as const;

/** a API respondeu 404 na rota nova: o servidor ainda é o de antes */
export class NotificationsUnavailable extends Error {
  constructor() {
    super('notifications_unavailable');
  }
}

async function getPage(query: Record<string, string | number | boolean | undefined>, signal?: AbortSignal) {
  const res = await api<NotificationsPage>('/notifications', { query, signal });
  if (res.error) {
    if (res.error.status === 404) throw new NotificationsUnavailable();
    throw res.error;
  }
  return res.data;
}

/** as linhas do sino e o contador (sem total — o sino não paga essa conta) */
export const notificationsBellQuery = () =>
  queryOptions({
    queryKey: [...NOTIFICATIONS_KEY, 'bell'] as const,
    queryFn: ({ signal }) => getPage({ per_page: NOTIFICATIONS_BELL_SIZE }, signal),
    // o realtime invalida a cada evento do tenant; o poll é a rede de segurança
    staleTime: 30_000,
    // 404 não melhora com nova tentativa: o servidor ainda não tem a rota
    retry: (count, err) => !(err instanceof NotificationsUnavailable) && count < 2,
  });

export interface NotificationsPageSearch {
  page: number;
  type?: NotificationEventKind;
  state: NotificationReadState;
}

export const NOTIFICATIONS_PAGE_SIZE = 30;

export const notificationsPageQuery = (search: NotificationsPageSearch) =>
  queryOptions({
    queryKey: [...NOTIFICATIONS_KEY, 'page', search] as const,
    queryFn: ({ signal }) =>
      getPage(
        { page: search.page, per_page: NOTIFICATIONS_PAGE_SIZE, type: search.type, state: search.state, total: 1 },
        signal
      ),
    placeholderData: keepPreviousData,
    retry: (count, err) => !(err instanceof NotificationsUnavailable) && count < 2,
  });

export const notificationRepliesQuery = () =>
  queryOptions({
    queryKey: [...NOTIFICATIONS_KEY, 'replies'] as const,
    queryFn: async ({ signal }): Promise<NotificationReplies> => {
      const res = await api<NotificationReplies>('/notifications/replies', { signal });
      if (res.error) {
        if (res.error.status === 404) throw new NotificationsUnavailable();
        throw res.error;
      }
      return res.data;
    },
    staleTime: 30_000,
    retry: (count, err) => !(err instanceof NotificationsUnavailable) && count < 2,
  });

/**
 * As duas escritas. O contador que volta é o do servidor; as listas se relêem
 * (o realtime e as outras abas da mesma pessoa também) — nada fica só aqui.
 */
export function useNotificationMarks() {
  const qc = useQueryClient();
  const settle = (data: NotificationsUnread) => {
    qc.setQueryData<NotificationsPage>([...NOTIFICATIONS_KEY, 'bell'], (old) =>
      old ? { ...old, unread: data.unread } : old
    );
    void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  };
  const readOne = useMutation({
    mutationFn: async (key: string) => {
      const res = await api<NotificationsUnread>('/notifications/read', { method: 'POST', body: { keys: [key] } });
      if (res.error) throw res.error;
      return res.data;
    },
    // otimista: a linha vira lida na hora; o POST confirma (ou o onError relê)
    onMutate: (key) => {
      qc.setQueriesData<NotificationsPage>(
        // o sino e a página — as Últimas respostas não têm lido
        { queryKey: NOTIFICATIONS_KEY, predicate: (q) => q.queryKey[2] === 'bell' || q.queryKey[2] === 'page' },
        (old) =>
        old
          ? {
              ...old,
              unread: old.items.some((i) => i.key === key && !i.read) ? Math.max(0, old.unread - 1) : old.unread,
              items: old.items.map((i) => (i.key === key ? { ...i, read: true } : i)),
            }
          : old
      );
    },
    onSuccess: settle,
    onError: () => void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
  const readAll = useMutation({
    /** `before`: o instante da notificação mais nova que a pessoa tinha na tela */
    mutationFn: async (before: string | undefined) => {
      const res = await api<NotificationsUnread>('/notifications/read-all', {
        method: 'POST',
        body: before ? { before } : {},
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: settle,
    onError: () => void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
  return { readOne, readAll };
}

/**
 * A marca antiga do sino ficava no navegador (`erp.notifSeenAt`) e fazia o
 * contador voltar noutra máquina. Ela não é lida mais; sai na primeira carga.
 */
export function forgetLegacySeenAt(): void {
  try {
    localStorage.removeItem('erp.notifSeenAt');
  } catch {
    // navegador sem localStorage: não há o que apagar
  }
}
